import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { InterestCollectionPreview } from '../../../services/request/DynamicInterest';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import AmountInput from '../../../shared-components/amount-input/AmountInput';

interface Props {
  open: boolean;
  loading: boolean;
  preview: InterestCollectionPreview | null;
  error?: string | null;
  onClose: () => void;
  onConfirm: (amount: number) => void;
  paymentReceivedBy: string;
  onPaymentReceivedByChange: (value: string) => void;
}

const InterestCollectionConfirmDialog: React.FC<Props> = ({
  open,
  loading,
  preview,
  error,
  onClose,
  onConfirm,
  paymentReceivedBy,
  onPaymentReceivedByChange,
}) => {
  const [collectAmount, setCollectAmount] = useState('');

  useEffect(() => {
    if (preview) {
      setCollectAmount(String(preview.collectibleAmount));
    } else {
      setCollectAmount('');
    }
  }, [preview]);

  const parsedAmount = parseFloat(collectAmount);
  const isValidAmount = !Number.isNaN(parsedAmount) && parsedAmount > 0;
  const calculatedAmount = preview?.collectibleAmount ?? 0;
  const isOverridden =
    preview != null && isValidAmount && Math.round(parsedAmount) !== calculatedAmount;

  const handleResetToCalculated = () => {
    if (preview) {
      setCollectAmount(String(preview.collectibleAmount));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm interest collection</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {preview && !loading && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Review the calculation below before collecting. You can adjust the amount if needed.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Row label="Monthly interest" value={formatCurrency(preview.monthlyInterest)} />
            <Row label="Per-day interest" value={formatCurrency(preview.perDayInterest)} />
            <Row
              label="Last collected"
              value={
                preview.lastInterestCollectedDate
                  ? new Date(preview.lastInterestCollectedDate).toLocaleDateString('en-IN')
                  : 'Not collected this cycle (using monthly pay day)'
              }
            />
            <Row
              label="Cycle starts from"
              value={new Date(preview.cycleStartDate || '').toLocaleDateString('en-IN')}
            />
            <Row label="Collection date" value={new Date(preview.collectionDate).toLocaleDateString('en-IN')} />
            <Row label="Elapsed days" value={String(preview.elapsedDays)} />
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2">Formula</Typography>
              <Typography variant="body2">{preview.formulaUsed}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ({formatCurrency(preview.monthlyInterest)} / 30) × {preview.elapsedDays} days ={' '}
                {formatCurrency(calculatedAmount)}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle2">Amount to collect</Typography>
                {isOverridden && (
                  <Button size="small" onClick={handleResetToCalculated}>
                    Reset to {formatCurrency(calculatedAmount)}
                  </Button>
                )}
              </Box>
              <AmountInput
                fullWidth
                label="Collectible amount"
                name="collectAmount"
                value={collectAmount}
                onChange={(e) => setCollectAmount(e.target.value)}
                helperText={
                  isOverridden
                    ? `Calculated: ${formatCurrency(calculatedAmount)} — you are collecting a different amount`
                    : `Suggested from formula: ${formatCurrency(calculatedAmount)}`
                }
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {preview.explanation}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Received by (optional)
              </Typography>
              <input
                style={{ width: '100%', padding: 8, fontSize: 14 }}
                value={paymentReceivedBy}
                onChange={(e) => onPaymentReceivedByChange(e.target.value)}
                placeholder="Name of person who received payment"
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onConfirm(Math.round(parsedAmount))}
          disabled={loading || !preview || !isValidAmount}
        >
          Collect {isValidAmount ? formatCurrency(parsedAmount) : 'interest'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box display="flex" justifyContent="space-between" py={0.5}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Box>
  );
}

export default InterestCollectionConfirmDialog;
